import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { AttackFlowStep, SeverityLevel } from "@/types/security";
import { getRiskColor } from "@/utils/securityHelpers";
import {
  Shield,
  Globe,
  Bot,
  Brain,
  Cpu,
  AlertTriangle,
  ArrowRight,
  X,
} from "lucide-react";

interface AttackGraphVisualProps {
  attackFlow: AttackFlowStep[];
  severity?: SeverityLevel;
}

const STATUS_STYLES: Record<string, { border: string; bg: string; icon: string; dot: string }> = {
  protected: {
    border: "border-emerald-300",
    bg: "bg-emerald-50/60",
    icon: "text-emerald-600",
    dot: "bg-emerald-500",
  },
  vulnerable: {
    border: "border-red-300",
    bg: "bg-red-50/60",
    icon: "text-red-600",
    dot: "bg-red-500",
  },
  warning: {
    border: "border-amber-300",
    bg: "bg-amber-50/60",
    icon: "text-amber-600",
    dot: "bg-amber-500",
  },
};

const RISK_ORDER: Record<string, number> = { critical: 3, high: 2, medium: 1, low: 0 };

const SEVERITY_TO_RISK: Record<string, string> = {
  CRITICAL: "critical",
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
  INFO: "low",
};

function getIcon(component: string) {
  const c = component.toLowerCase();
  if (c === "internet") return Globe;
  if (c === "agent" || c.includes("assistant")) return Bot;
  if (c === "model" || c.includes("llm") || c.includes("gpt")) return Brain;
  if (c === "detection" || c.includes("threat")) return AlertTriangle;
  if (c === "guard" || c.includes("shield")) return Shield;
  if (c.includes("intel")) return Cpu;
  return AlertTriangle;
}

function formatComponentLabel(component: string): string {
  const labels: Record<string, string> = {
    internet: "Internet",
    agent: "AI Agent",
    model: "LLM",
    detection: "Threat",
    guard: "Security",
    threat_intel: "Intel",
  };
  return labels[component.toLowerCase()] ?? component.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const COMPONENT_DESCRIPTIONS: Record<string, string> = {
  internet: "External entry point where the request originated. This is the initial attack surface exposed to untrusted input.",
  agent: "The AI agent that processed the request. Agents orchestrate LLM calls and may chain multiple operations.",
  model: "The language model that processed the input. Models can be manipulated through crafted prompts to produce harmful output.",
  detection: "A security scanner detected a threat at this stage. The flagged content matched known attack patterns.",
  guard: "Vortal's security controls intercepted and flagged this threat before it could cause further damage.",
  threat_intel: "Matched against known threat intelligence patterns from the MITRE ATLAS framework.",
};

/**
 * Ensure a risk level is at least as high as the severity floor.
 */
function atLeastRisk(risk: string, floor: string): AttackFlowStep["risk"] {
  if ((RISK_ORDER[floor] ?? 0) > (RISK_ORDER[risk] ?? 0)) {
    return floor as AttackFlowStep["risk"];
  }
  return risk as AttackFlowStep["risk"];
}

/**
 * Transform old-format attack flows where all nodes are `component: "llm_guard"`
 * into Wiz-style flows with distinct node types.  When `severity` is provided,
 * it acts as a floor so no node risk drops below the issue's actual severity.
 */
function normalizeAttackFlow(raw: AttackFlowStep[], severity?: SeverityLevel): AttackFlowStep[] {
  const severityFloor = SEVERITY_TO_RISK[severity ?? ""] ?? "medium";

  const allSameComponent = raw.length > 1 && raw.every((n) => n.component === raw[0].component);
  const isOldFormat = allSameComponent && raw[0].component.toLowerCase().includes("guard");

  if (!isOldFormat) {
    // New-format data — still apply severity floor to every node
    return raw.map((node) => ({
      ...node,
      risk: atLeastRisk(node.risk, severityFloor),
    }));
  }

  // Old-format: all nodes are "llm_guard" — rebuild as Wiz-style
  const highestRisk = raw.reduce(
    (best, n) => {
      return (RISK_ORDER[n.risk] ?? 0) > (RISK_ORDER[best] ?? 0) ? n.risk : best;
    },
    severityFloor,
  );

  const steps: AttackFlowStep[] = [];
  let stepNum = 1;

  steps.push({
    id: `synth_internet`,
    step: stepNum++,
    component: "internet",
    action: "Internet-facing endpoint",
    risk: highestRisk as AttackFlowStep["risk"],
    status: "warning",
  });

  for (const node of raw) {
    const action = node.action.replace(/ detected$/i, "");
    steps.push({
      ...node,
      id: `detection_${stepNum}`,
      step: stepNum++,
      component: "detection",
      action,
      risk: atLeastRisk(node.risk, severityFloor),
    });
  }

  steps.push({
    id: `synth_guard`,
    step: stepNum,
    component: "guard",
    action: "Flagged by Vortal",
    risk: highestRisk as AttackFlowStep["risk"],
    status: "protected",
  });

  return steps;
}

export function AttackGraphVisual({ attackFlow: rawFlow, severity }: AttackGraphVisualProps) {
  const attackFlow = useMemo(() => normalizeAttackFlow(rawFlow, severity), [rawFlow, severity]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = attackFlow.find((f) => f.id === selectedId);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Attack Path
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {attackFlow.length} steps
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Pipeline */}
        <div className="overflow-x-auto -mx-1 px-1 pb-2">
          <div className="flex items-stretch w-max gap-1">
            {attackFlow.map((flow, index) => {
              const styles = STATUS_STYLES[flow.status] ?? STATUS_STYLES.warning;
              const Icon = getIcon(flow.component);
              const isSelected = selectedId === flow.id;

              return (
                <div key={flow.id} className="flex items-center">
                  <button
                    onClick={() => setSelectedId(isSelected ? null : flow.id)}
                    className={`
                      relative flex flex-col p-2.5 rounded-lg border-2 transition-all
                      w-[140px] min-h-[90px]
                      ${styles.border} ${styles.bg}
                      ${isSelected ? "ring-2 ring-offset-1 ring-blue-400 shadow-md scale-[1.03]" : "hover:shadow-sm hover:scale-[1.01]"}
                    `}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <div
                        className={`w-5 h-5 rounded-full bg-white border-2 ${styles.border} flex items-center justify-center flex-shrink-0`}
                      >
                        <span className="text-[10px] font-bold tabular-nums">{flow.step}</span>
                      </div>
                      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${styles.icon}`} />
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 truncate">
                        {formatComponentLabel(flow.component)}
                      </span>
                    </div>

                    <p className="text-[11px] font-medium leading-tight text-gray-700 line-clamp-3 flex-1">
                      {flow.action}
                    </p>

                    <div className="mt-auto pt-1">
                      <span
                        className={`inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm ${getRiskColor(flow.risk)}`}
                      >
                        {flow.risk}
                      </span>
                    </div>
                  </button>

                  {index < attackFlow.length - 1 && (
                    <ArrowRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0 mx-0.5" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail panel for selected node */}
        {selected && (
          <div className="relative p-4 bg-slate-50 rounded-lg border text-sm animate-in fade-in-0 slide-in-from-top-1 duration-150">
            <button
              onClick={() => setSelectedId(null)}
              className="absolute top-2 right-2 p-0.5 rounded hover:bg-gray-200 text-gray-400"
            >
              <X className="h-3 w-3" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Step {selected.step}
              </span>
              <span className="text-[10px] text-gray-300">|</span>
              <span className="text-[10px] font-medium text-gray-500">
                {formatComponentLabel(selected.component)}
              </span>
              <Badge variant="outline" className="ml-auto text-[10px] capitalize">
                {selected.status}
              </Badge>
            </div>
            <p className="text-sm font-medium text-gray-800 mb-2">{selected.action}</p>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mb-2">
              <div>
                <dt className="text-[11px] text-muted-foreground">Risk Level</dt>
                <dd>
                  <span className={`inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-sm ${getRiskColor(selected.risk)}`}>
                    {selected.risk}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-muted-foreground">Status</dt>
                <dd className="text-xs capitalize">{selected.status}</dd>
              </div>
              {selected.technique_id && (
                <div>
                  <dt className="text-[11px] text-muted-foreground">Technique</dt>
                  <dd className="text-xs font-mono">{selected.technique_id}</dd>
                </div>
              )}
            </dl>
            {COMPONENT_DESCRIPTIONS[selected.component.toLowerCase()] && (
              <p className="text-xs text-gray-500 leading-relaxed border-t pt-2">
                {COMPONENT_DESCRIPTIONS[selected.component.toLowerCase()]}
              </p>
            )}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-4 pt-2 border-t">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">
            Legend
          </span>
          {(["protected", "warning", "vulnerable"] as const).map((status) => (
            <div key={status} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${STATUS_STYLES[status].dot}`} />
              <span className="text-[10px] text-gray-500 capitalize">{status}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
